import { BaseParser } from './BaseParser';
import {
  ValueType,
  IParseResult,
  ParseResult,
  CaseOption,
} from '../FormatterTypes';
import { formatterBoolParamDef } from '../Utils/ParamUtils';
import * as R from 'ramda';
import { replaceMultipleWords, isUppercaseLetter } from '../Utils/StringUtils';
import {
  isLowercaseLetter,
  isAlphaNumerical,
  capitalize,
  lowerize,
  isWhiteSpace,
  splitStringAt,
} from '../Utils/StringUtils';
import { isEmpty } from 'ramda';

export class TEDParseCase extends BaseParser {
  public static getInstance(): TEDParseCase {
    return this.instance ? this.instance : (this.instance = new this());
  }

  private static instance: TEDParseCase;

  defaultParams(): string {
    return 'ForceConvert="N";UpperCase="Y";LowerCase="Y";Words="Y";Paragraphs="N";PersNames="N";OrgNames="N";StreetNames="N"';
  }

  parse(text: string, params: string): IParseResult {
    let result = ParseResult.OK;
    let value: ValueType = '';

    const forceConvert = formatterBoolParamDef(
      'ForceConvert',
      params,
      this.defaultParams()
    );

    if (text !== '') {
      if (forceConvert || !textIsMixed(text)) {
        const opts = extractCaseOptions(params, this.defaultParams());
        value = convertCase(text, opts);
      } else {
        value = text;
      }
    }

    return {
      result,
      value,
    };
  }
}

const convertCase = (
  s: string,
  caseOptions: CaseOption[] = [CaseOption.words],
  from: number = 0,
  maxPos: number = Number.MAX_SAFE_INTEGER
): string => {
  maxPos = Math.min(maxPos, s.length - 1);
  from = Math.max(from, 0);

  while (from <= maxPos && isWhiteSpace(s[from])) {
    from++;
  }

  if (from <= maxPos) {
    let p = from;
    let len = maxPos - from + 1;

    if (
      !isEmpty(
        R.intersection(caseOptions, [
          CaseOption.persNames,
          CaseOption.orgNames,
          CaseOption.streetNames,
        ])
      )
    ) {
      caseOptions = R.union(
        R.intersection(caseOptions, [
          CaseOption.persNames,
          CaseOption.orgNames,
          CaseOption.streetNames,
        ]),
        [CaseOption.firstUpper, CaseOption.lower, CaseOption.words]
      );
      s = convertWithSeps(s, p, len, caseOptions, notDigitsLetters);
    } else if (
      !isEmpty(
        R.intersection(caseOptions, [CaseOption.words, CaseOption.paragraphs])
      ) &&
      !isEmpty(
        R.intersection(caseOptions, [
          CaseOption.firstUpper,
          CaseOption.firstLower,
        ])
      )
    ) {
      if (caseOptions.includes(CaseOption.words)) {
        convertWithSeps(s, p, len, caseOptions, notDigitsLetters);
      } else {
        convertWithSeps(s, p, len, caseOptions, paragraphSeparators);
      }
    } else {
      s = convert(s, p, len, caseOptions);
    }

    if (caseOptions.includes(CaseOption.persNames)) {
      s = replaceMultipleWords(
        s,
        ['Von', 'Af', 'Dödsbo'],
        ['von', 'af', 'dödsbo'],
        from,
        maxPos
      );
      if (s.startsWith('dödsbo')) {
        s = convert(s, p, 1, caseOptions);
      }
    }

    if (caseOptions.includes(CaseOption.orgNames)) {
      s = replaceMultipleWords(s, ['Ab'], ['AB'], from, maxPos);
    }

    if (caseOptions.includes(CaseOption.streetNames)) {
      s = replaceMultipleWords(
        s,
        roadNames.toReplace,
        roadNames.replaceWith,
        from,
        maxPos
      );
    }
  }

  return s;
};

const paragraphSeparators: IsSepFn = (c: string): boolean => {
  return /[.!?:;]/.test(c);
};

const notDigitsLetters: IsSepFn = (c: string): boolean => {
  return !isAlphaNumerical(c);
};

type IsSepFn = (c: string) => boolean;

const convertWithSeps = (
  s: string,
  p: number,
  len: number,
  caseOptions: CaseOption[],
  isSep: IsSepFn
): string => {
  let oldIndex = p;

  while (len > 0 && isSep(s[p])) {
    p++;
    len--;
  }

  while (len > 0 && /\s/.test(s[p])) {
    p++;
    len--;
  }

  while (len > 0) {
    oldIndex = p;
    while (len > 0 && !isSep(s[p])) {
      p++;
      len--;
    }
    s = convert(s, oldIndex, p - oldIndex + 1, caseOptions);
    while (len > 0 && isSep(s[p])) {
      p++;
      len--;
    }
    while (len > 0 && /\s/.test(s[p])) {
      p++;
      len--;
    }
  }

  return s;
};

const convert = (
  s: string,
  p: number,
  len: number,
  caseOptions: CaseOption[]
): string => {
  let { prefix, postfix, result } = splitStringAt(s, p, len);

  if (len > 0) {
    if (caseOptions.includes(CaseOption.firstUpper)) {
      result = result[0].toLocaleUpperCase() + result.slice(1);
      p++;
      len--;
    } else if (caseOptions.includes(CaseOption.firstLower)) {
      result = result[0].toLocaleLowerCase() + result.slice(1);
      p++;
      len--;
    }
  }

  if (len > 0) {
    if (caseOptions.includes(CaseOption.upper)) {
      result = capitalize(result, p, len);
    } else if (caseOptions.includes(CaseOption.lower)) {
      result = lowerize(result, p, len);
    }
  }

  return prefix + result + postfix;
};

const textIsMixed = (text: string) => {
  let lower: boolean = false;
  let upper: boolean = false;

  for (let char of text) {
    if (isUppercaseLetter(char)) {
      upper = true;
    } else if (isLowercaseLetter(char)) {
      lower = true;
    }

    if (upper && lower) {
      return true;
    }
  }

  return false;
};

const extractCaseOptions = (
  params: string,
  defaultParams: string
): CaseOption[] => {
  const getBoolParam = (name: string) =>
    formatterBoolParamDef(name, params, defaultParams);

  const co: CaseOption[] = [];
  if (getBoolParam('UpperCase')) {
    if (getBoolParam('LowerCase')) {
      co.push(CaseOption.firstUpper);
      co.push(CaseOption.lower);
    } else {
      co.push(CaseOption.upper);
    }
  } else {
    co.push(CaseOption.lower);
  }

  if (getBoolParam('Words')) {
    co.push(CaseOption.words);
  }

  if (getBoolParam('Paragraphs')) {
    co.push(CaseOption.paragraphs);
  }

  if (getBoolParam('PersNames')) {
    co.push(CaseOption.persNames);
  }

  if (getBoolParam('OrgNames')) {
    co.push(CaseOption.orgNames);
  }

  if (getBoolParam('StreetNames')) {
    co.push(CaseOption.streetNames);
  }

  return co;
};

const roadNames = {
  toReplace: [
    'Plats',
    'Stig',
    'Stigen',
    'Gata',
    'Gatan',
    'Väg',
    'Vägen',
    'Och',
    ':S',
    'Bv',
    'Tr',
    'Ög',
    'Läg',
    'Kv',
    'Ii',
    'Iii',
    'Iv',
    'Vi',
    'Vii',
    'Viii',
    'Ix',
    'Xi',
    'Xii',
    'Xiii',
    'Xiv',
    'Xv',
    'Xvi',
    'Xvii',
    'Xviii',
    'Xix',
    'Xx',
    'Lgh',
  ],
  replaceWith: [
    'plats',
    'stig',
    'stigen',
    'gata',
    'gatan',
    'väg',
    'vägen',
    'och',
    ':s',
    'BV',
    'TR',
    'ÖG',
    'LÄG',
    'KV',
    'II',
    'III',
    'IV',
    'VI',
    'VII',
    'VIII',
    'IX',
    'XI',
    'XII',
    'XIII',
    'XIV',
    'XV',
    'XVI',
    'XVII',
    'XVIII',
    'XIX',
    'XX',
    'lgh',
  ],
};
